import React, { useState } from 'react';
import styled from 'styled-components';
import { colors, fonts } from '../styles/globals';

const CTAContainer = styled.section`
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.darkBackground} 100%);
  color: ${colors.white};
  padding: 80px 20px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.2;
  }

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const CTATitle = styled.h2`
  font-family: ${fonts.playfair};
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  margin-bottom: 20px;
  line-height: 1.2;
  background: linear-gradient(135deg, ${colors.white} 0%, ${colors.accent} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const CTASubtitle = styled.p`
  font-family: ${fonts.inter};
  font-size: 1.2rem;
  font-weight: 500;
  margin-bottom: 30px;
  line-height: 1.5;
  opacity: 0.95;
`;

const GiftBox = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 25px;
  margin-bottom: 40px;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

const GiftTitle = styled.h3`
  font-family: ${fonts.playfair};
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 10px;
  color: ${colors.accent};
`;

const GiftDescription = styled.p`
  font-family: ${fonts.inter};
  font-size: 1rem;
  opacity: 0.9;
  line-height: 1.4;
`;

const FormContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 40px;
  max-width: 500px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 10px;
`;

const Checkbox = styled.input`
  margin-top: 4px;
  width: 18px;
  height: 18px;
  accent-color: ${colors.accent};
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-family: ${fonts.inter};
  font-size: 0.9rem;
  color: ${colors.white};
  line-height: 1.4;
  cursor: pointer;
  
  a {
    color: ${colors.accent};
    text-decoration: underline;
    transition: color 0.3s ease;
    
    &:hover {
      color: #E67E22;
    }
  }
`;

const Label = styled.label`
  font-family: ${fonts.inter};
  font-size: 0.9rem;
  font-weight: 600;
  color: ${colors.white};
  text-align: left;
`;

const Input = styled.input`
  font-family: ${fonts.inter};
  font-size: 1rem;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  color: ${colors.white};
  transition: all 0.3s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }

  &:focus {
    outline: none;
    border-color: ${colors.accent};
    background: rgba(255, 255, 255, 0.15);
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, ${colors.accent} 0%, #E67E22 100%);
  color: ${colors.white};
  font-family: ${fonts.inter};
  font-size: 1.1rem;
  font-weight: 600;
  padding: 18px 40px;
  border-radius: 50px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(243, 156, 107, 0.3);
  margin-top: 10px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 35px rgba(243, 156, 107, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 16px 32px;
  }
`;

const SuccessMessage = styled.div`
  background: rgba(76, 175, 80, 0.2);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 16px;
  padding: 20px;
  margin-top: 20px;
  color: #4CAF50;
  font-family: ${fonts.inter};
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  background: rgba(244, 67, 54, 0.2);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 16px;
  padding: 20px;
  margin-top: 20px;
  color: #F44336;
  font-family: ${fonts.inter};
  font-weight: 500;
`;

const CalendarContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 40px;
  max-width: 600px;
  margin: 0 auto;
  margin-top: 30px;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const CalendarTitle = styled.h3`
  font-family: ${fonts.playfair};
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 20px;
  color: ${colors.accent};
`;

const CalendarDescription = styled.p`
  font-family: ${fonts.inter};
  font-size: 1rem;
  margin-bottom: 30px;
  opacity: 0.9;
  line-height: 1.5;
`;

const CalendarIframe = styled.iframe`
  width: 100%;
  height: 600px;
  border: none;
  border-radius: 12px;
  background: white;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  color: ${colors.white};
  font-family: ${fonts.inter};
  font-size: 1rem;
  font-weight: 500;
  padding: 12px 24px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 20px;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

// Configuración de Brevo (anteriormente Sendinblue)
const BREVO_API_KEY = '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';
const BREVO_LIST_ID = 2; // ID de la lista en Brevo
const BREVO_TEMPLATE_ID = 1; // ID de la plantilla de email en Brevo

// Configuración de Brevo Meetings
const BREVO_MEETINGS_URL = 'https://meet.brevo.com/eduardo-gaoch/reunion-de-30-minutos';

function CTASection() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    acceptPrivacy: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle'); // 'idle' | 'success' | 'error'
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarData, setCalendarData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.acceptPrivacy) {
      setSubmitStatus('error');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Crear contacto en Brevo
      const contactResponse = await fetch('https://api.brevo.com/v3/contacts', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'api-key': BREVO_API_KEY
        },
        body: JSON.stringify({
          email: formData.email,
          attributes: {
            FIRSTNAME: formData.firstName,
            LASTNAME: formData.lastName,
            NOMBRE_COMPLETO: `${formData.firstName} ${formData.lastName}`.trim()
          },
          listIds: [BREVO_LIST_ID],
          updateEnabled: true
        })
      });

      if (contactResponse.ok || contactResponse.status === 204) {
        // Enviar email de bienvenida/lead magnet usando plantilla de Brevo
        const emailResponse = await fetch('https://api.brevo.com/v3/smtp/email', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'api-key': BREVO_API_KEY
          },
          body: JSON.stringify({
            to: [
              {
                email: formData.email,
                name: `${formData.firstName} ${formData.lastName}`.trim()
              }
            ],
            templateId: BREVO_TEMPLATE_ID,
            params: {
              FIRSTNAME: formData.firstName,
              NOMBRE_COMPLETO: `${formData.firstName} ${formData.lastName}`.trim()
            }
          })
        });

        if (emailResponse.ok) {
          setSubmitStatus('success');
          // Guardar datos para el calendario antes de limpiar el formulario
          setCalendarData({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email
          });
          setFormData({ firstName: '', lastName: '', email: '', acceptPrivacy: false });
          // Mostrar el calendario después de un breve delay
          setTimeout(() => {
            setShowCalendar(true);
          }, 2000);
        } else {
          // Si el contacto se creó pero el email falló, aún consideramos éxito
          setSubmitStatus('success');
          setCalendarData({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email
          });
          setFormData({ firstName: '', lastName: '', email: '', acceptPrivacy: false });
          setTimeout(() => {
            setShowCalendar(true);
          }, 2000);
        }
      } else {
        const errorData = await contactResponse.json();
        console.error('Error creating contact:', errorData);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToForm = () => {
    setShowCalendar(false);
    setSubmitStatus('idle');
    setCalendarData({ firstName: '', lastName: '', email: '' });
  };

  return (
    <CTAContainer id="cta-section">
      <CTAContent>
        <CTATitle>Agendar sesión + regalo inmediato</CTATitle>
        <CTASubtitle>
          Agenda ahora tu sesión gratuita y comienza el cambio
        </CTASubtitle>

        <GiftBox>
          <GiftTitle>Una sesión personalizada</GiftTitle>
          <GiftDescription>
            Para entender tu caso y mostrarte cómo "Habla Desde Tu Poder" puede ayudarte.
          </GiftDescription>
        </GiftBox>

        <GiftBox>
          <GiftTitle>Como regalo, recibirás automáticamente la guía exclusiva:</GiftTitle>
          <GiftDescription>
            "3 claves para liberar tu voz con autenticidad"
          </GiftDescription>
        </GiftBox>

        {!showCalendar ? (
          <FormContainer>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="firstName">Nombre *</Label>
                <Input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Tu nombre"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="lastName">Apellido *</Label>
                <Input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Tu apellido"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">Email *</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="tu@email.com"
                  required
                />
              </FormGroup>

              <CheckboxGroup>
                <Checkbox
                  type="checkbox"
                  id="acceptPrivacy"
                  name="acceptPrivacy"
                  checked={formData.acceptPrivacy}
                  onChange={handleInputChange}
                  required
                />
                <CheckboxLabel htmlFor="acceptPrivacy">
                  Acepto la <a href="/politica-privacidad" target="_blank">política de privacidad</a> y 
                  autorizo el tratamiento de mis datos personales para recibir información comercial.
                </CheckboxLabel>
              </CheckboxGroup>

              <SubmitButton 
                type="submit" 
                disabled={isSubmitting || !formData.acceptPrivacy}
              >
                {isSubmitting ? 'Enviando...' : 'Quiero liberar mi voz y recibir la guía'}
              </SubmitButton>
            </Form>

            {submitStatus === 'success' && (
              <SuccessMessage>
                ¡Gracias por agendar tu sesión! Revisa tu correo, allí tienes tu guía de regalo.
                Ahora puedes elegir la fecha y hora de tu sesión gratuita.
              </SuccessMessage>
            )}

            {submitStatus === 'error' && (
              <ErrorMessage>
                {!formData.acceptPrivacy 
                  ? 'Debes aceptar la política de privacidad para continuar.'
                  : 'Hubo un error al enviar el formulario. Por favor, intenta nuevamente.'
                }
              </ErrorMessage>
            )}
          </FormContainer>
        ) : (
          <CalendarContainer>
            <CalendarTitle>Elige la fecha y hora de tu sesión</CalendarTitle>
            <CalendarDescription>
              Selecciona el día y horario que mejor te convenga para tu sesión gratuita de 30 minutos.
              Recibirás un email de confirmación con los detalles de la videollamada.
            </CalendarDescription>
            
            <CalendarIframe
              src={`${BREVO_MEETINGS_URL}?firstName=${encodeURIComponent(calendarData.firstName)}&lastName=${encodeURIComponent(calendarData.lastName)}&email=${encodeURIComponent(calendarData.email)}&name=${encodeURIComponent(calendarData.firstName + ' ' + calendarData.lastName)}&first_name=${encodeURIComponent(calendarData.firstName)}&last_name=${encodeURIComponent(calendarData.lastName)}`}
              title="Calendario de Brevo Meetings"
              allow="camera; microphone; fullscreen; speaker; display-capture"
            />
            
            <BackButton onClick={handleBackToForm}>
              ← Volver al formulario
            </BackButton>
          </CalendarContainer>
        )}
      </CTAContent>
    </CTAContainer>
  );
}

export default CTASection; 